"use client";

import React from "react";

import { useTranslations } from "next-intl";

import { cn } from "@/utils/cn";

import { PropertyStatusBadge } from "../PropertyStatusBadge";
import { RealtorActionBar } from "../RealtorActionBar";
import { RealtorContextMenu } from "../RealtorContextMenu";

export interface RealtorActionsProps {
  /** Property ID */
  propertyId: string;
  /** Realtor ID */
  realtorId: string;
  /** Property status */
  status: string;
  /** CSS class name for custom styling */
  className?: string;
  /** Layout variant */
  variant?: "full" | "compact" | "minimal";
  /** Whether actions are disabled */
  disabled?: boolean;
  /** Show status badge */
  showStatus?: boolean;
  /** Show action bar */
  showActionBar?: boolean;
  /** Show context menu */
  showContextMenu?: boolean;
  /** Show dangerous actions in context menu */
  showDangerActions?: boolean;

  // Action callbacks
  /** Callback when edit is triggered */
  onEdit?: () => void;
  /** Callback when archive is triggered */
  onArchive?: () => void;
  /** Callback when promote is triggered */
  onPromote?: () => void;
  /** Callback when duplicate is triggered */
  onDuplicate?: () => void;
  /** Callback when delete is triggered */
  onDelete?: () => void;
  /** Callback when share is triggered */
  onShare?: () => void;
  /** Callback when view statistics is triggered */
  onViewStats?: () => void;

  /** Additional content to render */
  children?: React.ReactNode;
  /** Additional actions for action bar */
  additionalActions?: React.ReactNode;
  /** Additional menu items for context menu */
  additionalMenuItems?: React.ReactNode;
}

/**
 * RealtorActions - Main wrapper component for realtor property actions
 *
 * Combines PropertyStatusBadge, RealtorActionBar, and RealtorContextMenu
 * into a unified component with flexible layout options.
 *
 * Variants:
 * - full: Shows all components with full action bar
 * - compact: Shows status badge and compact action bar
 * - minimal: Shows only status badge and context menu
 *
 * @example
 * ```tsx
 * <RealtorActions
 *   propertyId="123"
 *   realtorId="456"
 *   status="active"
 *   variant="full"
 *   onEdit={() => navigate('/edit/123')}
 *   onArchive={() => archiveProperty('123')}
 *   showDangerActions={true}
 * />
 * ```
 */
export const RealtorActions: React.FC<RealtorActionsProps> = ({
  propertyId,
  realtorId,
  status,
  className,
  variant = "full",
  disabled = false,
  showStatus = true,
  showActionBar = true,
  showContextMenu = true,
  showDangerActions = false,
  onEdit,
  onArchive,
  onPromote,
  onDuplicate,
  onDelete,
  onShare,
  onViewStats,
  children,
  additionalActions,
  additionalMenuItems,
}) => {
  const t = useTranslations("realtor");

  // Configure component visibility based on variant
  const config = {
    full: {
      showStatus: true,
      showActionBar: true,
      showContextMenu: true,
      actionBarCompact: false,
    },
    compact: {
      showStatus: true,
      showActionBar: true,
      showContextMenu: true,
      actionBarCompact: true,
    },
    minimal: {
      showStatus: true,
      showActionBar: false,
      showContextMenu: true,
      actionBarCompact: false,
    },
  };

  const variantConfig = config[variant];
  const finalShowStatus = showStatus && variantConfig.showStatus;
  const finalShowActionBar = showActionBar && variantConfig.showActionBar;
  const finalShowContextMenu = showContextMenu && variantConfig.showContextMenu;

  return (
    <div
      className={cn(
        "flex items-center gap-3",
        {
          "flex-col space-y-3 sm:flex-row sm:space-y-0": variant === "full",
          "flex-row": variant === "compact" || variant === "minimal",
          "opacity-50": disabled,
        },
        className,
      )}
      data-testid="realtor-actions"
      role="group"
      aria-label={t("actions.groupLabel")}
    >
      {/* Status Badge */}
      {finalShowStatus && (
        <div className="flex-shrink-0">
          <PropertyStatusBadge
            status={status}
            size={variant === "compact" ? "sm" : "md"}
            data-testid="status-badge"
          />
        </div>
      )}

      {/* Action Bar */}
      {finalShowActionBar && (
        <div className="flex-grow min-w-0">
          <RealtorActionBar
            propertyId={propertyId}
            realtorId={realtorId}
            disabled={disabled}
            compact={variantConfig.actionBarCompact}
            onEdit={onEdit}
            onArchive={onArchive}
            onPromote={onPromote}
            onDuplicate={onDuplicate}
            additionalActions={additionalActions}
            data-testid="action-bar"
          />
        </div>
      )}

      {/* Context Menu */}
      {finalShowContextMenu && (
        <div className="flex-shrink-0">
          <RealtorContextMenu
            propertyId={propertyId}
            disabled={disabled}
            onEdit={onEdit}
            onArchive={onArchive}
            onPromote={onPromote}
            onDuplicate={onDuplicate}
            onDelete={onDelete}
            onShare={onShare}
            onViewStats={onViewStats}
            showDangerActions={showDangerActions}
            additionalItems={additionalMenuItems}
            data-testid="context-menu"
          />
        </div>
      )}

      {/* Additional Content */}
      {children && <div className="flex-shrink-0">{children}</div>}
    </div>
  );
};

export default RealtorActions;
